import React, { useEffect, useState } from "react";
import {
    Plus,
    Trash2,
    Edit3,
    CheckCircle2,
    XCircle,
    Eye,
    EyeOff,
    Save,
    X,
    Lock,
    CreditCard,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const BLANK = {
    name: "",
    slug: "",
    short_description: "",
    instructions: "",
    payment_details: {},
    reference_instructions: "",
    icon: "",
    enabled: false,
    visible_at_checkout: false,
    display_order: 100,
};

export const AdminPaymentMethodsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // { mode: "create"|"edit", data }

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/payment-methods", { headers });
            setMethods(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load payment methods");
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = async (m, patch) => {
        try {
            await api.post(`/admin/payment-methods/${m.id}/toggle`, patch, { headers });
            toast.success("Updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toggle failed");
        }
    };

    const remove = async (m) => {
        if (m.protected) {
            toast.error("This method is protected. Disable it instead of deleting.");
            return;
        }
        if (!window.confirm(`Delete "${m.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/payment-methods/${m.id}`, { headers });
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-payment-methods-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-extrabold text-white tracking-tight flex items-center gap-2">
                        <CreditCard size={22} className="text-[var(--gold)]" />
                        Payment Methods
                    </h2>
                    <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                        Manual payment options offered to customers at checkout
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        className="btn-neon"
                        data-testid="admin-pm-refresh"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        onClick={() => setEditor({ mode: "create", data: { ...BLANK } })}
                        className="btn-solid"
                        data-testid="admin-pm-add"
                    >
                        <Plus size={14} /> Add Payment Method
                    </button>
                </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-black/20 p-4 text-xs text-[var(--text-secondary)]">
                <p>
                    <strong className="text-white">Enabled</strong> is the master switch — when off, the method
                    is inert everywhere. <strong className="text-white">Visible at Checkout</strong> controls
                    whether customers see it (turn off for admin-only bookkeeping methods).
                </p>
            </div>

            {loading && (
                <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]">
                    Loading payment methods…
                </div>
            )}

            {!loading && methods.length === 0 && (
                <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]">
                    No payment methods configured yet. Click "Add Payment Method" to create one.
                </div>
            )}

            <div className="space-y-2" data-testid="admin-pm-list">
                {methods.map((m) => (
                    <MethodRow
                        key={m.id}
                        method={m}
                        onEdit={() => setEditor({ mode: "edit", data: { ...m } })}
                        onToggle={(patch) => toggle(m, patch)}
                        onDelete={() => remove(m)}
                    />
                ))}
            </div>

            {editor && (
                <MethodEditor
                    mode={editor.mode}
                    initial={editor.data}
                    onClose={() => setEditor(null)}
                    onSaved={() => {
                        setEditor(null);
                        load();
                    }}
                    headers={headers}
                />
            )}
        </div>
    );
};

const MethodRow = ({ method, onEdit, onToggle, onDelete }) => {
    return (
        <div
            className="glass-elevated rounded-2xl p-4 flex flex-wrap items-center gap-3 justify-between"
            data-testid={`admin-pm-row-${method.slug}`}
        >
            <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-bold text-white">{method.name}</span>
                    {method.protected && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] border border-white/10 rounded-full px-2 py-0.5">
                            <Lock size={10} /> Protected
                        </span>
                    )}
                    <span
                        className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                            method.enabled
                                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                : "border-white/10 text-[var(--text-muted)] bg-white/5"
                        }`}
                        data-testid={`pm-status-${method.slug}`}
                    >
                        {method.enabled ? "Enabled" : "Disabled"}
                    </span>
                    <span
                        className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                            method.visible_at_checkout
                                ? "border-[var(--gold)]/40 text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                : "border-white/10 text-[var(--text-muted)] bg-white/5"
                        }`}
                        data-testid={`pm-visibility-${method.slug}`}
                    >
                        {method.visible_at_checkout ? "Visible at Checkout" : "Hidden from Checkout"}
                    </span>
                </div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">
                    <span className="font-mono">{method.slug}</span>
                    {method.short_description && ` · ${method.short_description}`}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    onClick={() => onToggle({ enabled: !method.enabled })}
                    className={`px-3 py-1.5 rounded-full border text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                        method.enabled
                            ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
                            : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    }`}
                    data-testid={`pm-toggle-enabled-${method.slug}`}
                >
                    {method.enabled ? <><XCircle size={12} /> Disable</> : <><CheckCircle2 size={12} /> Enable</>}
                </button>
                <button
                    onClick={() => onToggle({ visible_at_checkout: !method.visible_at_checkout })}
                    disabled={!method.enabled}
                    className={`px-3 py-1.5 rounded-full border text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                        !method.enabled
                            ? "border-white/5 text-[var(--text-muted)] opacity-50 cursor-not-allowed"
                            : method.visible_at_checkout
                                ? "border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[rgba(233,188,11,0.1)]"
                                : "border-white/20 text-white hover:bg-white/5"
                    }`}
                    title={!method.enabled ? "Enable the method first" : ""}
                    data-testid={`pm-toggle-visible-${method.slug}`}
                >
                    {method.visible_at_checkout ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show at Checkout</>}
                </button>
                <button
                    onClick={onEdit}
                    className="px-3 py-1.5 rounded-full border border-white/10 text-[11px] font-mono uppercase tracking-widest text-white hover:bg-white/5 inline-flex items-center gap-1"
                    data-testid={`pm-edit-${method.slug}`}
                >
                    <Edit3 size={12} /> Edit
                </button>
                {!method.protected && (
                    <button
                        onClick={onDelete}
                        className="px-3 py-1.5 rounded-full border border-red-500/30 text-[11px] font-mono uppercase tracking-widest text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1"
                        data-testid={`pm-delete-${method.slug}`}
                    >
                        <Trash2 size={12} /> Delete
                    </button>
                )}
            </div>
        </div>
    );
};

const MethodEditor = ({ mode, initial, onClose, onSaved, headers }) => {
    const [form, setForm] = useState({
        ...initial,
        payment_details_text: kvToText(initial.payment_details || {}),
    });
    const [busy, setBusy] = useState(false);

    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required");
            return;
        }
        const payload = {
            name: form.name.trim(),
            short_description: form.short_description || "",
            instructions: form.instructions || "",
            payment_details: textToKv(form.payment_details_text || ""),
            reference_instructions: form.reference_instructions || "",
            icon: form.icon || "",
            enabled: !!form.enabled,
            visible_at_checkout: !!form.visible_at_checkout,
            display_order: Number(form.display_order) || 100,
        };
        setBusy(true);
        try {
            if (mode === "create") {
                if (form.slug) payload.slug = form.slug;
                await api.post("/admin/payment-methods", payload, { headers });
                toast.success("Payment method created");
            } else {
                await api.put(`/admin/payment-methods/${form.id}`, payload, { headers });
                toast.success("Payment method updated");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-auto p-4"
            onClick={onClose}
            data-testid="admin-pm-editor"
        >
            <div
                className="glass-elevated rounded-2xl w-full max-w-2xl p-6 relative my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 text-[var(--text-muted)] hover:text-white"
                    aria-label="Close editor"
                    data-testid="admin-pm-editor-close"
                >
                    <X size={18} />
                </button>
                <h3 className="font-display text-xl font-bold text-white mb-4">
                    {mode === "create" ? "Add Payment Method" : `Edit — ${form.name}`}
                </h3>

                <div className="space-y-3">
                    <Field label="Payment Method Name *">
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => upd("name", e.target.value)}
                            placeholder="e.g. BPAY"
                            className="input-dark"
                            data-testid="pm-field-name"
                        />
                    </Field>
                    {mode === "create" && (
                        <Field
                            label="Slug (optional)"
                            hint="Machine-readable id used in URLs & order records. Auto-generated if blank."
                        >
                            <input
                                type="text"
                                value={form.slug}
                                onChange={(e) => upd("slug", e.target.value.toLowerCase())}
                                placeholder="e.g. bpay"
                                className="input-dark font-mono"
                                data-testid="pm-field-slug"
                            />
                        </Field>
                    )}
                    <Field label="Short Description" hint="Shown next to the method at checkout.">
                        <input
                            type="text"
                            value={form.short_description}
                            onChange={(e) => upd("short_description", e.target.value)}
                            placeholder="Direct deposit · 1 business day"
                            className="input-dark"
                            data-testid="pm-field-short"
                        />
                    </Field>
                    <Field label="Payment Instructions" hint="Body text explaining how to pay.">
                        <textarea
                            value={form.instructions}
                            onChange={(e) => upd("instructions", e.target.value)}
                            rows={4}
                            className="input-dark min-h-[100px]"
                            data-testid="pm-field-instructions"
                        />
                    </Field>
                    <Field
                        label="Payment Details"
                        hint="Key: Value pairs, one per line. Shown to customers on the order page and email."
                    >
                        <textarea
                            value={form.payment_details_text}
                            onChange={(e) => upd("payment_details_text", e.target.value)}
                            rows={4}
                            placeholder="BSB: 062-000
Account Number: 12345678
Account Name: Carbon Labs"
                            className="input-dark font-mono text-sm min-h-[100px]"
                            data-testid="pm-field-details"
                        />
                    </Field>
                    <Field label="Payment Reference Instructions">
                        <input
                            type="text"
                            value={form.reference_instructions}
                            onChange={(e) => upd("reference_instructions", e.target.value)}
                            placeholder="Use the order reference as your transfer description."
                            className="input-dark"
                            data-testid="pm-field-reference"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Icon" hint="smartphone · landmark · wallet · card · bag">
                            <select
                                value={form.icon || ""}
                                onChange={(e) => upd("icon", e.target.value)}
                                className="input-dark"
                                data-testid="pm-field-icon"
                            >
                                <option value="" className="bg-black">— none —</option>
                                {["smartphone", "landmark", "wallet", "card", "bag"].map((k) => (
                                    <option key={k} value={k} className="bg-black">
                                        {k}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Display Order" hint="Lower numbers appear first.">
                            <input
                                type="number"
                                value={form.display_order}
                                onChange={(e) => upd("display_order", e.target.value)}
                                className="input-dark"
                                data-testid="pm-field-order"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.enabled}
                                onChange={(e) => upd("enabled", e.target.checked)}
                                data-testid="pm-field-enabled"
                            />
                            <span className="text-sm text-white">Enabled</span>
                        </label>
                        <label className={`flex items-center gap-2 ${form.enabled ? "cursor-pointer" : "opacity-60"}`}>
                            <input
                                type="checkbox"
                                checked={!!form.visible_at_checkout}
                                disabled={!form.enabled}
                                onChange={(e) => upd("visible_at_checkout", e.target.checked)}
                                data-testid="pm-field-visible"
                            />
                            <span className="text-sm text-white">Visible at Checkout</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-white hover:bg-white/5"
                        data-testid="pm-editor-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={busy}
                        className="btn-solid"
                        data-testid="pm-editor-save"
                    >
                        <Save size={14} /> {busy ? "Saving…" : "Save Payment Method"}
                    </button>
                </div>
            </div>
            <style>{`
                .input-dark {
                    width: 100%;
                    background: rgba(0,0,0,0.4);
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 12px;
                    padding: 10px 14px;
                    color: white;
                    font-size: 14px;
                }
                .input-dark:focus {
                    outline: none;
                    border-color: var(--gold);
                }
            `}</style>
        </div>
    );
};

const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">
            {label}
        </label>
        {children}
        {hint && <div className="text-[11px] text-[var(--text-muted)] mt-1">{hint}</div>}
    </div>
);

// Parse "Key: Value" lines into an object.
function textToKv(text) {
    const out = {};
    for (const line of (text || "").split(/\r?\n/)) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const idx = trimmed.indexOf(":");
        if (idx === -1) continue;
        const key = trimmed
            .slice(0, idx)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
        const value = trimmed.slice(idx + 1).trim();
        if (key) out[key] = value;
    }
    return out;
}

function kvToText(obj) {
    return Object.entries(obj || {})
        .map(([k, v]) => `${k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: ${v}`)
        .join("\n");
}
